import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { BrandingProvider } from '@lemontaigu/platform-ui';
import { createSupabaseBrandingStore } from '@lemontaigu/platform/adapters/client';
import { supabase } from './supabase.js';

/**
 * The branding composition root for the reception desk cockpit (reception.lemontaigu.com) (docs/UI-BRANDING.md B4–B6).
 *
 * Binds the concrete Supabase client to the `BrandingStore` port and names this
 * app's slug — `reception` — which is the key its chrome colour is looked up under
 * in `app_tenant.brand`. The slug is the ONE thing about branding that is
 * legitimately compiled in: it identifies which app this binary is, not what
 * the app looks like or who runs it.
 *
 * Named `*Provider.tsx` because that is the convention eslint.config.js matches
 * to recognise a SPA composition root — the one kind of file allowed to bind a
 * concrete client to a port.
 *
 * Mounted inside `AuthGate`: `app_tenant` is read under RLS narrowed to
 * `actor_tenants()`, so it needs a session. Before sign-in the shell keeps its
 * compiled fallback, which is B2 working as intended — the sign-in card shows
 * the app name and no organisation.
 */
export function AppBrandingProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => createSupabaseBrandingStore(supabase), []);
  return (
    <BrandingProvider store={store} app="reception">
      {children}
    </BrandingProvider>
  );
}
